import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Stack, Text } from '@bonusly/design-system';
import { useBuildRecognizeURL } from '@bonusly/url';
import { useAnalytics } from '@bonusly/who-am-i';

import { Bonus, SearchBonus } from '../../../../types/bonus';
import { Route } from '../../config/router';
import { useKeyboardListControl } from '../../hooks/useKeyboardListControl';
import { SearchUser } from '../../types/user';
import { pathWithId } from '../../utils/api';
import { BonusCard } from '../Bonus';
import { UserCard } from '../User';
import { EventName } from './analytics';
import { useSearchContext } from './context';
import { SearchResult } from './SearchResult';
import { toBonusType, toUserType } from './utils';

export const isSearchBonus = (record: unknown): record is SearchBonus => !!record && typeof record === 'object' && 'amount' in record;

export const RecordsPanel: FC = () => {
	const recognizeURL = useBuildRecognizeURL();
	const { bonuses, query, users } = useSearchContext();
	const { t } = useTranslation('navigation');

	const options = useMemo(() => [...users, ...bonuses], [users, bonuses]);
	const bonusURL = (bonus: Bonus): string => recognizeURL('bonuses', bonus.id);

	const onSelectOption = (option: SearchBonus | SearchUser) => {
		window.location.assign(isSearchBonus(option) ? bonusURL(toBonusType(option)) : recognizeURL(pathWithId(Route.UserProfile, option.objectID)));
	};

	const { isSelected, register } = useKeyboardListControl<SearchBonus | SearchUser, HTMLAnchorElement>({
		emitClickOnSelect: true,
		focusSelected: true,
		onSelectOption,
		options,
		selectOnTab: false,
	});

	const { track } = useAnalytics();

	return (
		<Box>
			<Text color="gray.900" fontSize="md" fontWeight="medium" pl={3} mb={2}>
				{t('search.users_tab')}
			</Text>

			<Stack as="ul" listStyleType="none" mb={3}>
				{users?.map((user) => {
					const { onClick, ...props } = register(user);

					return (
						<SearchResult
							aria-label={user.display_name}
							onClick={() => {
								track(EventName.UserClick);
								onClick();
							}}
							key={user.objectID}
							isSelected={isSelected(user)}
							{...props}
						>
							<UserCard user={toUserType(user)} />
						</SearchResult>
					);
				})}
			</Stack>

			<Text color="gray.900" fontSize="md" fontWeight="medium" pl={3} mb={2}>
				{t('search.recognition_tab')}
			</Text>

			<Stack as="ul" listStyleType="none">
				{bonuses?.map((bonus) => {
					const { onClick, ...props } = register(bonus);
					return (
						<SearchResult
							aria-label={`Recognition by ${bonus.giver.display_name}`}
							isSelected={isSelected(bonus)}
							key={bonus.objectID}
							onClick={() => {
								track(EventName.RecognitionClick);
								onClick();
							}}
							{...props}
						>
							<BonusCard bonus={toBonusType(bonus)} highlight={query} />
						</SearchResult>
					);
				})}
			</Stack>
		</Box>
	);
};
